/**
 * Клиент публичного каталога и расчёта (E07.1–E07.6, E09 price/bom/заявки).
 */

import type {
  CalcPreviewRequest,
  CalcPreviewResult,
  ClientAccessRequest,
  ClientAccessResult,
  ClientCabinetAccessGrantRequest,
  ClientLoginRequest,
  CreateProjectRequest,
  ProjectStep,
  ProjectSummary,
  PublicAccessGrantRequest,
  PublicAccessGrantRequestResult,
  PublicBomPreviewRequest,
  PublicBomPreviewResult,
  PublicDrivePreviewRequest,
  PublicDrivePreviewResult,
  PublicIndustry,
  PublicModelCard,
  PublicModelForm,
  PublicPricePreviewRequest,
  PublicPricePreviewResult,
  PublicTourSlide,
  RegisterClientRequest,
  RegisterClientResult,
  SaveProjectStepRequest,
} from "@potok/shared";
import { apiRequest } from "./http";

export type PublicCatalogApi = {
  listIndustries(): Promise<PublicIndustry[]>;
  listModels(industryId: string | null): Promise<PublicModelCard[]>;
  listTour(industryId: string | null): Promise<PublicTourSlide[]>;
  getModelForm(modelId: string): Promise<PublicModelForm>;
  previewCalc(request: CalcPreviewRequest): Promise<CalcPreviewResult>;
  previewPrice(request: PublicPricePreviewRequest): Promise<PublicPricePreviewResult>;
  previewBom(request: PublicBomPreviewRequest): Promise<PublicBomPreviewResult>;
  previewDrive(request: PublicDrivePreviewRequest): Promise<PublicDrivePreviewResult>;
  requestAccessGrant(
    projectId: string,
    request: PublicAccessGrantRequest,
  ): Promise<PublicAccessGrantRequestResult>;
  requestCabinetAccessGrant(
    request: ClientCabinetAccessGrantRequest,
  ): Promise<PublicAccessGrantRequestResult>;
  createProject(request?: CreateProjectRequest): Promise<ProjectSummary>;
  saveProjectStep(projectId: string, request: SaveProjectStepRequest): Promise<ProjectStep>;
  registerClient(projectId: string, request: RegisterClientRequest): Promise<RegisterClientResult>;
  /** Регистрация без гостевого проекта (отдельная страница). */
  registerClientStandalone(request: RegisterClientRequest): Promise<RegisterClientResult>;
  accessClient(request: ClientAccessRequest): Promise<ClientAccessResult>;
  loginClient(request: ClientLoginRequest): Promise<ClientAccessResult>;
};

export const httpPublicCatalogApi: PublicCatalogApi = {
  listIndustries: () => apiRequest<PublicIndustry[]>("/industries"),
  listModels: (industryId) => {
    const query = industryId === null ? "" : `?industryId=${encodeURIComponent(industryId)}`;
    return apiRequest<PublicModelCard[]>(`/models${query}`);
  },
  listTour: (industryId) => {
    const query = industryId === null ? "" : `?industryId=${encodeURIComponent(industryId)}`;
    return apiRequest<PublicTourSlide[]>(`/tour${query}`);
  },
  getModelForm: (modelId) => apiRequest<PublicModelForm>(`/models/${modelId}/form`),
  previewCalc: (body) =>
    apiRequest<CalcPreviewResult>("/calc/preview", { method: "POST", body }),
  previewPrice: (body) =>
    apiRequest<PublicPricePreviewResult>("/calc/price-preview", { method: "POST", body }),
  previewBom: (body) =>
    apiRequest<PublicBomPreviewResult>("/calc/bom-preview", { method: "POST", body }),
  previewDrive: (body) =>
    apiRequest<PublicDrivePreviewResult>("/calc/drive-preview", { method: "POST", body }),
  requestAccessGrant: (projectId, body) =>
    apiRequest<PublicAccessGrantRequestResult>(`/projects/${projectId}/access-grants`, {
      method: "POST",
      body,
    }),
  requestCabinetAccessGrant: (body) =>
    apiRequest<PublicAccessGrantRequestResult>("/clients/access-grants", {
      method: "POST",
      body,
    }),
  createProject: (request) => apiRequest<ProjectSummary>("/projects", { method: "POST", body: request }),
  saveProjectStep: (projectId, body) =>
    apiRequest<ProjectStep>(`/projects/${projectId}/steps`, { method: "PUT", body }),
  registerClient: (projectId, body) =>
    apiRequest<RegisterClientResult>(`/projects/${projectId}/register`, { method: "POST", body }),
  registerClientStandalone: (body) =>
    apiRequest<RegisterClientResult>("/clients/register", { method: "POST", body }),
  accessClient: (body) => apiRequest<ClientAccessResult>("/clients/access", { method: "POST", body }),
  loginClient: (body) => apiRequest<ClientAccessResult>("/clients/login", { method: "POST", body }),
};
